import { computed, ref } from "vue";

import { findActiveBracketPair } from "@/helpers/parser";
import {
  formulaOffsetToNodePosition,
  nodePositionToFormulaOffset,
} from "@/helpers/parser/cursorMapping";
import { useEditorSerializer } from "@/hooks/useEditorSerializer";

import type { ParsedFormulaMetadata } from "@/helpers/parser/visitor";
import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";
import type { Ref } from "vue";

export const useEditorParserIntegration = (
  editorContent: Ref<EditorNode[]>,
  functionsByNameMap: Ref<Record<string, FormulaOption>>,
  serviceAttributes: Ref<IAttribute[]>
) => {
  const metadata = ref<ParsedFormulaMetadata>({
    bracketPairs: new Map(),
    functionCalls: [],
    totalTokens: 0,
  });

  const parseErrors = ref<string[]>([]);

  const serializer = useEditorSerializer(
    () => functionsByNameMap.value,
    () => serviceAttributes.value
  );

  const updateContentWithParser = async (
    newFormula: string,
    preserveIdentities = true
  ): Promise<EditorNode[]> => {
    const { nodes, parseResult } = serializer.deserialize(
      newFormula,
      editorContent.value,
      { preserveIdentities, fallbackOnError: false }
    );

    metadata.value = parseResult.metadata;
    parseErrors.value = parseResult.errors.map((err) => err.message);

    return nodes;
  };

  const serializeToFormula = (): string => {
    return serializer.serialize(editorContent.value);
  };

  const insertTextAtCursor = async (
    text: string,
    nodeIndex: number,
    nodeOffset: number
  ): Promise<{
    newNodes: EditorNode[];
    newNodeIndex: number;
    newNodeOffset: number;
  }> => {
    const currentFormula = serializeToFormula();
    const cursorOffset = nodePositionToFormulaOffset(
      editorContent.value,
      nodeIndex,
      nodeOffset
    );

    const newFormula =
      currentFormula.slice(0, cursorOffset) +
      text +
      currentFormula.slice(cursorOffset);

    const newNodes = await updateContentWithParser(newFormula);

    const newSerializedFormula = serializer.serialize(newNodes);
    const lengthDiff = newSerializedFormula.length - newFormula.length;
    const targetOffsetInSpacedFormula = cursorOffset + text.length + lengthDiff;

    const newCursorPosition = formulaOffsetToNodePosition(
      newNodes,
      targetOffsetInSpacedFormula
    );

    return {
      newNodes,
      newNodeIndex: newCursorPosition.index,
      newNodeOffset: newCursorPosition.offset,
    };
  };

  const deleteTextRange = async (
    startNodeIndex: number,
    startOffset: number,
    endNodeIndex: number,
    endOffset: number
  ): Promise<{ newNodes: EditorNode[]; newCursorOffset: number }> => {
    const currentFormula = serializeToFormula();
    const startFormulaOffset = nodePositionToFormulaOffset(
      editorContent.value,
      startNodeIndex,
      startOffset
    );
    const endFormulaOffset = nodePositionToFormulaOffset(
      editorContent.value,
      endNodeIndex,
      endOffset
    );

    const newFormula =
      currentFormula.slice(0, startFormulaOffset) +
      currentFormula.slice(endFormulaOffset);

    const newNodes = await updateContentWithParser(newFormula);

    return { newNodes, newCursorOffset: startFormulaOffset };
  };

  const getCursorPosition = (
    formulaOffset: number
  ): { index: number; offset: number } => {
    return formulaOffsetToNodePosition(editorContent.value, formulaOffset);
  };

  const getActiveBracketPair = (cursorNodeIndex: number) => {
    return findActiveBracketPair(cursorNodeIndex, metadata.value);
  };

  const hasParseErrors = computed(() => parseErrors.value.length > 0);

  return {
    metadata,
    parseErrors,
    hasParseErrors,
    updateContentWithParser,
    serializeToFormula,
    insertTextAtCursor,
    deleteTextRange,
    getCursorPosition,
    getActiveBracketPair,
  };
};
